import { forwardRef, useState, useRef } from 'react'
import {
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch'
import { ZOOM_STEP } from '@app/constant'
import { ZoomableImageContent } from '@app/components/common/imageMagnifier/components/ZoomableImageContent'

const PAN_STEP = 56
const MIN_SCALE = 1
const MAX_SCALE = 2

type PanDirection = 'up' | 'down' | 'left' | 'right'
type TransformBounds = {
  minPositionX: number
  maxPositionX: number
  minPositionY: number
  maxPositionY: number
}

const getPannedPosition = (
  positionX: number,
  positionY: number,
  direction: PanDirection,
) => {
  switch (direction) {
    case 'left':
      return { positionX: positionX + PAN_STEP, positionY }
    case 'right':
      return { positionX: positionX - PAN_STEP, positionY }
    case 'up':
      return { positionX, positionY: positionY + PAN_STEP }
    case 'down':
      return { positionX, positionY: positionY - PAN_STEP }
  }
}

const roundPosition = (value: number) => Math.round(value * 100) / 100

const clampPosition = (value: number, min: number, max: number) =>
  roundPosition(Math.min(Math.max(value, min), max))

const clampScale = (scale: number) =>
  Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale))

const getBoundedPosition = (
  position: { positionX: number; positionY: number },
  bounds?: TransformBounds | null,
) => {
  if (!bounds) return position

  return {
    positionX: clampPosition(
      position.positionX,
      bounds.minPositionX,
      bounds.maxPositionX,
    ),
    positionY: clampPosition(
      position.positionY,
      bounds.minPositionY,
      bounds.maxPositionY,
    ),
  }
}

const getCenteredZoomPosition = (
  transformApi: ReactZoomPanPinchRef,
  transformState: { scale: number; positionX: number; positionY: number },
  newScale: number,
) => {
  const { wrapperComponent } = transformApi.instance
  if (!wrapperComponent || transformState.scale === 0) {
    return {
      positionX: transformState.positionX,
      positionY: transformState.positionY,
    }
  }

  const scaleDifference = newScale - transformState.scale
  const centerX = wrapperComponent.offsetWidth / 2
  const centerY = wrapperComponent.offsetHeight / 2
  const focalPointX = (centerX - transformState.positionX) / transformState.scale
  const focalPointY = (centerY - transformState.positionY) / transformState.scale

  return {
    positionX: roundPosition(
      transformState.positionX - focalPointX * scaleDifference,
    ),
    positionY: roundPosition(
      transformState.positionY - focalPointY * scaleDifference,
    ),
  }
}

const getCurrentBounds = (
  transformApi: ReactZoomPanPinchRef,
  scale: number,
): TransformBounds | null | undefined => {
  const { bounds, contentComponent, setup, wrapperComponent } =
    transformApi.instance

  if (!wrapperComponent || !contentComponent) return bounds

  const wrapperWidth = wrapperComponent.offsetWidth
  const wrapperHeight = wrapperComponent.offsetHeight
  const contentWidth = contentComponent.offsetWidth
  const contentHeight = contentComponent.offsetHeight
  const scaledContentWidth = contentWidth * scale
  const scaledContentHeight = contentHeight * scale
  const diffWidth = wrapperWidth - scaledContentWidth
  const diffHeight = wrapperHeight - scaledContentHeight
  const centerZoomedOut = Boolean(setup?.centerZoomedOut)
  const scaleWidthFactor =
    wrapperWidth > scaledContentWidth
      ? diffWidth * (centerZoomedOut ? 1 : 0.5)
      : 0
  const scaleHeightFactor =
    wrapperHeight > scaledContentHeight
      ? diffHeight * (centerZoomedOut ? 1 : 0.5)
      : 0

  return {
    minPositionX: wrapperWidth - scaledContentWidth - scaleWidthFactor,
    maxPositionX: scaleWidthFactor,
    minPositionY: wrapperHeight - scaledContentHeight - scaleHeightFactor,
    maxPositionY: scaleHeightFactor,
  }
}

const getTransformState = (transformApi: ReactZoomPanPinchRef) =>
  transformApi.instance?.transformState ?? transformApi.state

type ZoomImageContainerProps = {
  src?: string
  alt?: string
  className?: string
  tabIndex?: number
  children?: string | JSX.Element | JSX.Element[]
  isFullscreen: boolean
  setOpenFullImage: (status: boolean) => void
  iconColor?: string
  fullscreenButtonRef?: React.RefObject<HTMLButtonElement>
}

export const ZoomImageContainer = forwardRef<any, ZoomImageContainerProps>(
  ({ isFullscreen, setOpenFullImage, fullscreenButtonRef, ...props }, ref) => {
    const [imageScale, setImageScale] = useState<number>(1)
    const containerRef = useRef<ReactZoomPanPinchRef | null>(null)

    const handleChangeFullscreen = (status: boolean) => {
      setOpenFullImage(status)
    }

    const handleZoomInOut =
      (decrease = false) =>
      () => {
        if (containerRef.current) {
          const { setTransform } = containerRef.current
          const transformState = getTransformState(containerRef.current)
          if (!transformState) return

          const {
            scale = 1,
            positionY = 0,
            positionX = 0,
          } = transformState
          const newScale = clampScale(
            Math.round((decrease ? scale - ZOOM_STEP : scale + ZOOM_STEP) * 10) / 10,
          )
          const centeredZoomPosition = getCenteredZoomPosition(
            containerRef.current,
            { scale, positionX, positionY },
            newScale,
          )
          const { positionX: nextPositionX, positionY: nextPositionY } =
            getBoundedPosition(
              centeredZoomPosition,
              getCurrentBounds(containerRef.current, newScale),
            )

          setImageScale(newScale)
          setTransform(nextPositionX, nextPositionY, newScale, 0)
        }
      }

    const handleResetZoom = () => {
      if (containerRef.current) {
        const { setTransform } = containerRef.current
        const { positionX, positionY } = getBoundedPosition(
          { positionX: 0, positionY: 0 },
          getCurrentBounds(containerRef.current, 1),
        )

        setImageScale(1)
        setTransform(positionX, positionY, 1, 0)
      }
    }

    const handlePan = (direction: PanDirection) => () => {
      if (!containerRef.current) return

      const { setTransform } = containerRef.current
      const transformState = getTransformState(containerRef.current)
      if (!transformState || (transformState.scale ?? 1) <= 1) return

      const { positionX, positionY } = getBoundedPosition(
        getPannedPosition(
          transformState.positionX ?? 0,
          transformState.positionY ?? 0,
          direction,
        ),
        getCurrentBounds(containerRef.current, transformState.scale ?? 1),
      )

      setTransform(positionX, positionY, transformState.scale ?? 1, 0)
    }

    return (
      <TransformWrapper
        initialScale={1}
        maxScale={MAX_SCALE}
        minScale={MIN_SCALE}
        doubleClick={{ disabled: true }}
        wheel={{ disabled: true }}
        limitToBounds={true}
        centerOnInit={true}
        ref={containerRef}
      >
        <ZoomableImageContent
          ref={ref}
          onFullscreenChange={handleChangeFullscreen}
          isFullscreen={isFullscreen}
          fullscreenButtonRef={fullscreenButtonRef}
          imageScale={imageScale}
          handleZoomIn={handleZoomInOut(false)}
          handleZoomOut={handleZoomInOut(true)}
          handleResetZoom={handleResetZoom}
          canPan={imageScale > MIN_SCALE}
          handlePan={handlePan}
          {...props}
        />
      </TransformWrapper>
    )
  },
)
